'use client'

import { useEffect, useRef, useState } from 'react'
import Image from 'next/image'
import { useTranslations } from 'next-intl'
import { Icon } from '@iconify/react/dist/iconify.js'
import { MentorType } from '@/app/types/mentor'
import { countryToISO } from '@/lib/timezone'

// Not anchored to the start and doesn't require a protocol — handles
// youtube.com/watch?v=, youtu.be/, m.youtube.com/watch?v=, and
// youtube.com/shorts/ links, with or without "https://" in front.
function getYoutubeEmbedUrl(url: string): string | null {
  const match = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([a-zA-Z0-9_-]{6,})/)
  return match ? `https://www.youtube.com/embed/${match[1]}?autoplay=1` : null
}

function fmtDuration(seconds: number): string {
  if (!Number.isFinite(seconds)) return '0:00'
  const m = Math.floor(seconds / 60)
  const s = Math.floor(seconds % 60)
  return `${m}:${String(s).padStart(2, '0')}`
}

// The photo-forward "media card" — portrait bleeds to the card's edges, a
// verified badge sits on the image itself, and the watch/listen buttons
// float where photo meets content (straddling the seam, layered) instead
// of tucked into a corner. Each badge only renders when that tutor
// actually has the media — no badge promising a video/recording that
// isn't really there. Shared between the homepage carousel
// (Home/Tutors/index.tsx) and the /tutors directory grid — same visual
// identity everywhere a tutor shows up as a card.
export default function TutorCard({ tutor, onInfoClick, infoLoading, size = 'default' }: {
  tutor: MentorType
  // Optional — when provided, the name/title block at the bottom becomes
  // its own button (e.g. the course page's "Teachers for This Course"
  // wires this to navigate to the full profile). Never wraps the whole
  // card: the photo's own play/watch buttons above it need to keep
  // working as their own separate clicks, not bubble up into this.
  onInfoClick?: () => void
  // Shows a spinner in place of the name/title while onInfoClick's own
  // navigation is in flight — the caller tracks this, not this component
  // (it doesn't know whether onInfoClick navigates, opens something else,
  // or is instant).
  infoLoading?: boolean
  // 'default' (w-65, 260px) is the homepage carousel's own size — /tutors
  // uses 'compact' for a somewhat smaller tile in its grid, same style and
  // functionality either way, just narrower (the aspect-ratio photo box
  // and the flex-wrap grid it sits in both scale with the card's own
  // width automatically; only this one class actually changes).
  size?: 'default' | 'compact'
}) {
  const t = useTranslations('Home.tutors')
  const [openMedia, setOpenMedia] = useState<'audio' | 'video' | null>(null)
  const [isPlaying, setIsPlaying] = useState(false)
  const [currentTime, setCurrentTime] = useState(0)
  const [duration, setDuration] = useState(0)
  // toggleFullscreen puts the BOX (mediaBoxRef) into fullscreen, not the
  // <video> element itself, so a plain CSS [&:fullscreen] variant on the
  // video can't see that state — this element isn't the one that's
  // actually fullscreen. Tracking it in JS instead lets the thumbnail stay
  // object-cover (fills the card nicely) while fullscreen switches to
  // object-contain (shows the whole frame, no cropping).
  const [isFullscreen, setIsFullscreen] = useState(false)
  const mediaBoxRef = useRef<HTMLDivElement>(null)
  const audioRef = useRef<HTMLAudioElement>(null)
  // An uploaded video (tutor.videoUrl) always wins over a YouTube link
  // when both are set — self-hosted, so a visitor never has to leave to
  // the teacher's own channel; the YouTube embed only ever plays as a
  // fallback for a teacher who hasn't had a video uploaded yet.
  const embedUrl = tutor.youtubeUrl ? getYoutubeEmbedUrl(tutor.youtubeUrl) : null
  const hasVideo = !!tutor.videoUrl || !!embedUrl
  const hasMedia = !!tutor.audioUrl || hasVideo
  const countryIso = countryToISO(tutor.country)

  // Video opens as an overlay on the card's own photo — not a page-wide
  // popup — with a fullscreen control (audio's own bar doesn't get one; a
  // simple player doesn't need it).
  function toggleFullscreen() {
    if (document.fullscreenElement) { document.exitFullscreen(); return }
    mediaBoxRef.current?.requestFullscreen()
  }

  function openAudio() {
    setOpenMedia('audio')
  }

  function openVideo() {
    audioRef.current?.pause()
    setOpenMedia('video')
  }

  function closeAudio() {
    audioRef.current?.pause()
    setOpenMedia(null)
  }

  function toggleAudioPlay() {
    const el = audioRef.current
    if (!el) return
    // .play()'s own promise rejects (uncaught, unless caught here) if a
    // pause() interrupts it before it resolves — e.g. a fast double-tap on
    // this same button, or closeAudio()/openVideo() firing right after
    // (error_logs #158: "The play() request was interrupted by a call to
    // pause()"). Nothing to do about it — the element just ends up paused
    // either way, which is already the intended end state.
    if (isPlaying) el.pause()
    else el.play().catch(() => {})
  }

  // Click-to-seek on the progress track — a lightweight custom bar (no
  // extra library) built on the <audio> element's own currentTime/
  // duration, styled to read like YouTube's player rather than the
  // browser's native (and, per browser, wildly inconsistent) controls UI.
  function seekAudio(e: React.MouseEvent<HTMLDivElement>) {
    const el = audioRef.current
    if (!el || !duration) return
    const rect = e.currentTarget.getBoundingClientRect()
    const pct = Math.min(1, Math.max(0, (e.clientX - rect.left) / rect.width))
    el.currentTime = pct * duration
  }

  useEffect(() => {
    function onFullscreenChange() { setIsFullscreen(document.fullscreenElement === mediaBoxRef.current) }
    document.addEventListener('fullscreenchange', onFullscreenChange)
    return () => document.removeEventListener('fullscreenchange', onFullscreenChange)
  }, [])

  useEffect(() => {
    if (!openMedia) return
    function onKey(e: KeyboardEvent) { if (e.key === 'Escape') setOpenMedia(null) }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [openMedia])

  // Starts playback ourselves instead of the <audio> element's own autoPlay
  // attribute — that way this is a real, explicit .play() call we can
  // .catch() (see toggleAudioPlay's own comment on why that matters: a
  // visitor tapping close/watch-video fast enough to interrupt it before it
  // resolves is a real, observed case, not a hypothetical one).
  useEffect(() => {
    if (openMedia === 'audio') audioRef.current?.play().catch(() => {})
  }, [openMedia])

  return (
    <div className={`group relative ${size === 'compact' ? 'w-52' : 'w-65'} rounded-3xl overflow-hidden bg-white border border-gray-100 shadow-[0_10px_30px_-12px_rgba(17,24,39,0.12)] hover:shadow-[0_20px_45px_-15px_rgba(12,144,84,0.28)] hover:-translate-y-1.5 transition-all duration-300`}>
      {/* Gold/green sweep — the same top-accent treatment the pricing cards
          use, so every card family on the site reads as one system. */}
      <div className='h-1.5 bg-linear-to-r from-secondary via-primary to-secondary' />

      <div ref={mediaBoxRef} className='relative aspect-4/5 overflow-hidden bg-primary/5'>
        {tutor.imgSrc ? (
          <Image
            src={tutor.imgSrc}
            alt={tutor.name}
            fill
            sizes='(max-width: 640px) 80vw, 320px'
            className='object-cover group-hover:scale-105 transition-transform duration-500'
          />
        ) : (
          <div className='absolute inset-0 flex items-center justify-center text-primary text-5xl font-bold'>
            {tutor.name.charAt(0).toUpperCase()}
          </div>
        )}

        <span className='absolute top-3 left-3 flex items-center gap-1 bg-white/90 backdrop-blur-sm rounded-full pl-1.5 pr-2.5 py-1 shadow-sm'>
          <Icon icon='solar:verified-check-bold' width={14} className='text-primary' />
          <span className='text-[10px] font-bold text-gray-700 uppercase tracking-wide'>{t('verified')}</span>
        </span>

        {/* Country flag — opt-in via tutor.country (see MentorType's own
            comment); same flagcdn source as LiveClock.tsx/TutorLocalTime.tsx. */}
        {countryIso && (
          <span title={tutor.country ?? undefined} className='absolute top-3 right-3 bg-white/90 backdrop-blur-sm rounded-full p-1 shadow-sm'>
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img src={`https://flagcdn.com/24x18/${countryIso}.png`} alt={tutor.country ?? ''} width={20} height={15} className='rounded-xs block' />
          </span>
        )}

        {/* Video overlay — covers the whole photo (you need to see the
            video). requestFullscreen() targets this same box, so
            fullscreen shows exactly this overlay full-screen. An uploaded
            video plays inline (self-hosted); only a teacher with no
            uploaded video falls back to the YouTube embed. */}
        {openMedia === 'video' && hasVideo && (
          <div className='absolute inset-0 z-20 bg-black flex flex-col'>
            <div className='flex items-center justify-end gap-1.5 p-2 shrink-0'>
              <button
                type='button'
                onClick={toggleFullscreen}
                aria-label={t('fullscreen')}
                className='w-8 h-8 rounded-full bg-white/15 hover:bg-white/25 text-white flex items-center justify-center transition-colors'
              >
                <Icon icon='mdi:fullscreen' width={18} />
              </button>
              <button
                type='button'
                onClick={() => setOpenMedia(null)}
                aria-label={t('close')}
                className='w-8 h-8 rounded-full bg-white/15 hover:bg-white/25 text-white flex items-center justify-center transition-colors'
              >
                <Icon icon='material-symbols:close-rounded' width={18} />
              </button>
            </div>

            <div className='flex-1 min-h-0'>
              {tutor.videoUrl ? (
                // object-cover in the card (fills the box nicely);
                // object-contain once isFullscreen is true (see its own
                // comment above) — a video whose aspect ratio doesn't
                // match the viewer's screen would otherwise get cropped
                // when this box fills the whole display; letterboxing
                // (the box is already bg-black, so the bars just blend
                // in) keeps the whole frame visible instead.
                // controlsList/onContextMenu — hides the browser's own
                // download button and blocks "Save Video As…"; this is a
                // deterrent only (the file's URL is necessarily public
                // either way) — admins watermark the file themselves
                // before uploading it, nothing burns one in server-side.
                <video src={tutor.videoUrl} controls autoPlay className={`w-full h-full ${isFullscreen ? 'object-contain' : 'object-cover'}`}
                  controlsList='nodownload' onContextMenu={e => e.preventDefault()} />
              ) : (
                <iframe
                  src={embedUrl ?? undefined}
                  className='w-full h-full'
                  allow='autoplay; encrypted-media'
                  allowFullScreen
                  title={`${tutor.name} demo video`}
                />
              )}
            </div>
          </div>
        )}

        {/* Audio bar — only the bottom strip, so the photo stays visible
            behind/above it instead of being covered like the video does.
            A small custom-styled bar (play/pause, scrubber, time) instead
            of the browser's native <audio controls> skin — every browser
            renders that natively very differently, and none of them can be
            restyled with real CSS, so matching the site's own look (and
            the YouTube-style layout this was asked to read as) means
            driving a plain <audio> element with our own markup instead. No
            extra library — just the element's own currentTime/duration. */}
        {openMedia === 'audio' && tutor.audioUrl && (
          <div className='absolute inset-x-0 bottom-0 z-20 bg-linear-to-t from-black/90 via-black/75 to-transparent pt-8 pb-3 px-3'>
            <div className='flex items-center justify-end mb-1'>
              <button
                type='button'
                onClick={closeAudio}
                aria-label={t('close')}
                className='w-7 h-7 rounded-full bg-white/15 hover:bg-white/25 text-white flex items-center justify-center transition-colors'
              >
                <Icon icon='material-symbols:close-rounded' width={15} />
              </button>
            </div>

            <audio
              ref={audioRef}
              controlsList='nodownload noremoteplayback'
              onContextMenu={e => e.preventDefault()}
              src={tutor.audioUrl}
              onPlay={() => setIsPlaying(true)}
              onPause={() => setIsPlaying(false)}
              onEnded={() => setIsPlaying(false)}
              onLoadedMetadata={e => setDuration(e.currentTarget.duration)}
              onTimeUpdate={e => setCurrentTime(e.currentTarget.currentTime)}
              className='hidden'
            />

            <div className='flex items-center gap-2'>
              <button
                type='button'
                onClick={toggleAudioPlay}
                aria-label={isPlaying ? t('pause') : t('play')}
                className='shrink-0 w-7 h-7 rounded-full bg-white text-black flex items-center justify-center'
              >
                <Icon icon={isPlaying ? 'mdi:pause' : 'mdi:play'} width={15} />
              </button>

              <span className='text-[10px] text-white/80 tabular-nums shrink-0 w-8 text-right'>
                {fmtDuration(currentTime)}
              </span>

              <div
                onClick={seekAudio}
                className='relative flex-1 h-3 flex items-center cursor-pointer group/seek'
              >
                <div className='w-full h-1 rounded-full bg-white/25 overflow-hidden'>
                  <div
                    className='h-full bg-primary'
                    style={{ width: `${duration ? (currentTime / duration) * 100 : 0}%` }}
                  />
                </div>
                <div
                  className='absolute top-1/2 -translate-y-1/2 -translate-x-1/2 w-2.5 h-2.5 rounded-full bg-primary opacity-0 group-hover/seek:opacity-100 transition-opacity'
                  style={{ left: `${duration ? (currentTime / duration) * 100 : 0}%` }}
                />
              </div>

              <span className='text-[10px] text-white/80 tabular-nums shrink-0 w-8'>
                {fmtDuration(duration)}
              </span>
            </div>
          </div>
        )}
      </div>

      {/* Watch/listen buttons — pulled up by half their own height so they
          straddle the photo/content boundary instead of being tucked into
          the photo's own corner. Positioned via negative margin (not
          absolute+calc against the aspect-ratio box), so they stay exactly
          on the seam regardless of the card's actual rendered width. */}
      {hasMedia && !openMedia && (
        <div className='relative z-10 flex justify-center gap-2 -mt-5.5'>
          {tutor.audioUrl && (
            <button
              type='button'
              onClick={openAudio}
              aria-label={t('playRecitation')}
              className='w-11 h-11 rounded-full bg-white shadow-md border border-gray-100 text-primary flex items-center justify-center hover:scale-110 transition-transform'
            >
              <Icon icon='ph:waveform-bold' width={20} />
            </button>
          )}
          {hasVideo && (
            <button
              type='button'
              onClick={openVideo}
              aria-label={t('watchVideo')}
              className='w-11 h-11 rounded-full bg-white shadow-md border border-gray-100 flex items-center justify-center hover:scale-110 transition-transform'
            >
              <Image src='/images/tutors/youtube.svg' alt='' width={22} height={22} />
            </button>
          )}
        </div>
      )}

      {onInfoClick ? (
        <button type='button' onClick={onInfoClick} disabled={infoLoading} aria-busy={infoLoading}
          className={`relative w-full px-4 text-center disabled:opacity-70 ${hasMedia && !openMedia ? 'pt-2' : 'pt-5'} pb-5`}>
          {infoLoading ? (
            <Icon icon='mdi:loading' width={20} className='animate-spin text-primary mx-auto' />
          ) : (
            <>
              <h6 className={size === 'compact' ? 'text-sm' : 'text-lg'}>{tutor.name}</h6>
              <p className={`font-medium text-primary/80 pt-1 ${size === 'compact' ? 'text-xs' : 'text-sm'}`}>{tutor.profession}</p>
            </>
          )}
        </button>
      ) : (
        <div className={`relative px-4 text-center ${hasMedia && !openMedia ? 'pt-2' : 'pt-5'} pb-5`}>
          <h6 className={size === 'compact' ? 'text-sm' : 'text-lg'}>{tutor.name}</h6>
          <p className={`font-medium text-primary/80 pt-1 ${size === 'compact' ? 'text-xs' : 'text-sm'}`}>{tutor.profession}</p>
        </div>
      )}
    </div>
  )
}
